import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Trash2, ShoppingBag, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import { formatDateDisplay } from "../lib/utils";

export default function CartPage() {
  const {
    items, updateQty, removeFromCart, subtotal, shipping, digitalProcessingFee, total,
    preOrderItems, updatePreOrderQty, removeFromPreOrderCart, preOrderSubtotal,
  } = useCart();

  const hasRegular = items.length > 0;
  const hasPreOrder = preOrderItems.length > 0;
  const formatMoney = (value) => value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  if (!hasRegular && !hasPreOrder) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-16 h-16 text-gold/40 mx-auto mb-6" />
        <h1 className="text-3xl font-black uppercase tracking-tighter">Your cart is empty</h1>
        <p className="text-slate-400 mt-2">Time to load up on some serious gear.</p>
        <Link to="/shop" className="inline-block mt-6 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3 rounded-full hover:bg-gold-light btn-glow transition-all">Start Shopping</Link>
      </div>
    );
  }

  return (
    <div>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <PageHero
        eyebrow="Cart"
        title={<>Your <span className="text-gold">cart</span></>}
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Cart" }]}
        size="compact"
      />
      <div className="site-container py-10">

      <div className="space-y-12">
        {/* ── Regular Cart ── */}
        {hasRegular && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag className="w-4 h-4 text-gold" />
              <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold">Regular Cart</h2>
            </div>
            <div className="grid lg:grid-cols-[1fr_360px] gap-10">
              <div className="space-y-3" data-testid="cart-items-list">
                {items.map((it) => (
                  <div key={it.product_id} className="flex gap-4 p-4 bg-navy-700 rounded-xl neon-border">
                    {it.image && <img src={it.image} alt="" className="w-24 h-24 object-cover rounded-lg" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-gold/70">{it.platform} · {it.category === "digital" ? "Digital Code" : it.condition}</div>
                      <Link to={`/product/${it.product_id}`} className="font-bold text-sm hover:text-gold line-clamp-2">{it.name}</Link>
                      <div className="text-gold font-black mt-1">₹{it.price.toLocaleString()}</div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => removeFromCart(it.product_id)} className="text-slate-400 hover:text-rose-400" data-testid="cart-remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="inline-flex items-center bg-navy-800 rounded-full overflow-hidden">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-3 py-1 hover:text-gold">−</button>
                        <span className="px-3 text-sm font-bold">{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-3 py-1 hover:text-gold">+</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <aside className="bg-navy-700 rounded-xl p-6 neon-border h-fit">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-5">Order Summary</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-bold">₹{formatMoney(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span className="font-bold">{shipping === 0 ? "FREE" : `₹${shipping}`}</span></div>
                  {digitalProcessingFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Digital processing (2.5%)</span>
                      <span className="font-bold">₹{formatMoney(digitalProcessingFee)}</span>
                    </div>
                  )}
                  <div className="border-t border-gold/20 my-3" />
                  <div className="flex justify-between text-base"><span className="font-extrabold uppercase">Total</span><span className="font-black text-gold text-xl" data-testid="cart-total">₹{formatMoney(total)}</span></div>
                </div>
                <Link to="/checkout" data-testid="checkout-button" className="block w-full text-center mt-6 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all">
                  Proceed to Checkout
                </Link>
                {shipping > 0 && subtotal < 4999 && <div className="mt-3 text-[11px] text-center text-slate-400">Add ₹{formatMoney(4999 - subtotal)} more for free shipping</div>}
              </aside>
            </div>
          </section>
        )}

        {/* ── Pre-Order Cart ── */}
        {hasPreOrder && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-gold" />
              <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold">Pre-Order Cart</h2>
              <span className="bg-gold/10 border border-gold/30 text-gold text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">1 product max</span>
            </div>
            <div className="grid lg:grid-cols-[1fr_360px] gap-10">
              <div className="space-y-3">
                {preOrderItems.map((it) => (
                  <div key={it.product_id} className="flex gap-4 p-4 bg-navy-700 rounded-xl border border-gold/40">
                    {it.image && <img src={it.image} alt="" className="w-24 h-24 object-cover rounded-lg" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-gold text-navy-900 text-[9px] font-black uppercase tracking-[0.15em] px-2 py-0.5 rounded">PRE-ORDER</span>
                        <span className="text-[10px] uppercase tracking-wider text-gold/70">{it.platform}</span>
                      </div>
                      <Link to={`/product/${it.product_id}`} className="font-bold text-sm hover:text-gold line-clamp-2">{it.name}</Link>
                      <div className="text-gold font-black mt-1">₹{it.price.toLocaleString()}</div>
                      {it.release_date && (
                        <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Releases {formatDateDisplay(it.release_date)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => removeFromPreOrderCart(it.product_id)} className="text-slate-400 hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="inline-flex items-center bg-navy-800 rounded-full overflow-hidden">
                        <button onClick={() => updatePreOrderQty(it.product_id, it.quantity - 1)} className="px-3 py-1 hover:text-gold">−</button>
                        <span className="px-3 text-sm font-bold">{it.quantity}</span>
                        <button onClick={() => updatePreOrderQty(it.product_id, it.quantity + 1)} className="px-3 py-1 hover:text-gold">+</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <aside className="bg-navy-700 rounded-xl p-6 border border-gold/40 h-fit">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-5">Pre-Order Summary</h2>
                <div className="space-y-2 text-sm">
                  {(() => {
                    const poShipping = preOrderSubtotal > 4999 || preOrderSubtotal === 0 ? 0 : 100;
                    return <>
                      <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-bold">₹{preOrderSubtotal.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span className="font-bold">{poShipping === 0 ? "FREE" : `₹${poShipping}`}</span></div>
                      <div className="border-t border-gold/20 my-3" />
                      <div className="flex justify-between text-base"><span className="font-extrabold uppercase">Total</span><span className="font-black text-gold text-xl">₹{(preOrderSubtotal + poShipping).toLocaleString()}</span></div>
                      {preOrderSubtotal < 4999 && preOrderSubtotal > 0 && <div className="text-[11px] text-center text-slate-400">Add ₹{(4999 - preOrderSubtotal).toLocaleString()} more for free shipping</div>}
                    </>;
                  })()}
                </div>
                <Link to="/preorder-checkout" className="block w-full text-center mt-4 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all">
                  Pre-Book Now
                </Link>
              </aside>
            </div>
          </section>
        )}
      </div>
      </div>
    </div>
  );
}
